import React from "react";
import { motion } from "framer-motion";
import { IoRestaurant, IoPeople, IoCalendar, IoCall, IoMail } from "react-icons/io5";
import { DecorativeElement } from "../../components/Decorative";

const CateringPage = () => {
  const regularMenu = {
    appetizers: [
      "Medhu Vada",
      "Masala Vada",
      "Idli Sambar",
      "Mango Garlic Chicken Dry",
      "Tamilnadu 65 Chicken / Paneer",
      "Chukka Chicken / Goat / Lamb",
      "Tawa Fry Chicken / Fish",
      "Finger Fry Chicken / Fish",
      "Pepper Dry Chicken / Goat",
      "Kola Urundai Goat / Chicken",
    ],
    indoChinese: [
      "Chilli Paneer",
      "Chilli Chicken",
      "Fried Rice - Veg / Egg / Chicken / Lamb",
      "Fried Idli - Veg / Egg / Chicken / Lamb",
    ],
    idliDosaIdiyappam: [
      "Podi Idli",
      "Mini Idly",
      "Plain Dosa",
      "Masala Dosa",
      "Kari Dosa - Chicken / Goat / Lamb",
      "Idiyappam with Special Coconut Milk",
    ],
    kothu: [
      "Kothu Poratta - Veg / Egg / Chicken / Tuna",
      "Kothu Idiyappam - Veg / Egg / Chicken / Tuna",
    ],
    briyaniRice: [
      "Hyderabadi Briyani - Veg / Chicken / Goat / Lamb",
      "Chennai Special Briyani - Veg / Chicken / Goat",
      "White Rice",
    ],
    curryEntree: [
      "Tikka Masala - Paneer or Chicken",
      "Butter Masala - Paneer or Chicken",
      "White Korma - Veg / Chicken / Paneer",
      "Lychee Curry - Paneer / Chicken",
      "Curry - Veg / Chicken / Goat",
      "Banana Curry - Chicken / Lamb",
      "Urundai Gravy - Goat / Chicken",
      "Saag - Veg / Chicken / Goat / Paneer",
      "Fish Curry / Shrimp Curry",
      "Dal Tadka",
      "Channa Masala",
    ],
    tandoor: [
      "Chicken Tandoori",
      "Chicken Tikka",
      "Fish Tikka / Shrimp",
    ],
    breads: [
      "Naan / Garlic Naan / Sweet Naan",
      "Tandoori Roti",
      "Onion Kulcha",
      "Mixed Paneer Naan",
      "Chapati",
    ],
    desserts: [
      "Halwa - Wheat / Bread / Carrot",
      "Marriage Kesari",
      "Sweet Pongal",
    ],
    drinks: [
      "Lassi - Mango / Rose",
      "Soda - Diet Coke / Ginger Ale / Sprite",
    ],
    condiments: [
      "Chutney (3 varieties)",
      "Sambar",
      "Brinjal Gravy",
    ],
  };

  const specialMenu = {
    appetizers: [
      "Bhajji Fritters",
      "Nandhi Chicken",
      "Special Chicken Dry",
      "Banana Chicken Dry",
      "Fish 65",
      "Fish Puttu",
      "Chicken Vadai",
      "Samosa - Veg / Lamb",
    ],
    curry: [
      "Miloni Tarkari",
      "Chole Bhutra",
      "Paneer Labbadar",
      "Paneer Bhurji",
      "Chicken Curry Leaf Special",
      "Goat Curry Leaf Special",
      "Pepper Masala - Chicken / Goat / Lamb",
      "Kanniyakumari Chicken Gravy",
      "Kanniyakumari Fish Gravy",
      "Madurai Idli / Kudal",
      "Poratta Salna - Veg / Chicken",
      "Shrimp Masala",
      "Shrimp Perratal",
      "Shrimp Thokku",
      "Chicken Pallipalyam",
      "Chicken Sinthamani",
      "Kongu Gravy - Chicken / Goat",
    ],
    tandoor: [
      "Lamb Tandoor",
      "Chicken Kariyali Kebab",
      "Tandoor Shrimp",
    ],
    briyani: [
      "Dhalapakatti Briyani",
      "Pickle Briyani",
      "Kongu Briyani",
      "Mandhi Briyani (Indo/Arab)",
      "Fish Briyani",
    ],
    rice: [
      "Tamarind / Lemon Rice",
      "Ginger Garlic Rice",
      "Paruppu Sadham",
      "Green Rice - Coriander Leaf / Spinach / Pudina",
    ],
    desserts: [
      "Jigardhanda",
      "Mango Ice Cream",
      "Ilaneer Paysam",
      "Urad Dhal Halwa",
    ],
    breads: [
      "Poori Masala",
      "Chilli Garlic Naan",
      "Goat Sukka Naan",
    ],
    drinks: [
      "Coffee",
      "Katan Chai",
      "Elachi Tea",
      "Ginger Tea",
    ],
  };

  const services = [
    {
      icon: <IoRestaurant className="text-4xl text-primary" />,
      title: "Full Service Catering",
      description: "Complete setup, service staff, and cleanup included",
    },
    {
      icon: <IoPeople className="text-4xl text-primary" />,
      title: "Drop-off Catering",
      description: "Perfect for office meetings and small gatherings",
    },
    {
      icon: <IoCalendar className="text-4xl text-primary" />,
      title: "Event Planning",
      description: "Let us help plan your perfect event",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-primary to-primaryDark text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Catering Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl mb-8 max-w-3xl mx-auto"
          >
            From intimate gatherings to grand celebrations, we bring the
            authentic flavors of India to your special occasions
          </motion.p>
          <motion.a
            href="tel:+18576055777"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="inline-block bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
          >
            Call Now: (857) 605-5777
          </motion.a>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-headingColor mb-4">
              Our Catering Services
            </h2>
            <p className="text-textColor">
              Customizable options to suit your needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex flex-col items-center text-center">
                  {service.icon}
                  <h3 className="text-xl font-semibold text-headingColor mt-4">
                    {service.title}
                  </h3>
                  <p className="mt-2 text-textColor">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Regular Menu Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-headingColor mb-4">
              Our Catering Menu
            </h2>
            <p className="text-lg text-textColor max-w-2xl mx-auto">
              Choose from our extensive selection of authentic Indian dishes
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Appetizers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow"
            >
              <h3 className="text-2xl font-bold text-primary mb-4 border-b-2 border-primary pb-2">
                Appetizers
              </h3>
              <ul className="space-y-2">
                {regularMenu.appetizers.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Indo Chinese */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow"
            >
              <h3 className="text-2xl font-bold text-primary mb-4 border-b-2 border-primary pb-2">
                Indo Chinese
              </h3>
              <ul className="space-y-2">
                {regularMenu.indoChinese.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Idli/Dosa/Idiyappam */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow"
            >
              <h3 className="text-2xl font-bold text-primary mb-4 border-b-2 border-primary pb-2">
                Idli / Dosa / Idiyappam
              </h3>
              <ul className="space-y-2">
                {regularMenu.idliDosaIdiyappam.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Kothu */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow"
            >
              <h3 className="text-2xl font-bold text-primary mb-4 border-b-2 border-primary pb-2">
                Kothu
              </h3>
              <ul className="space-y-2">
                {regularMenu.kothu.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Briyani/Rice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow"
            >
              <h3 className="text-2xl font-bold text-primary mb-4 border-b-2 border-primary pb-2">
                Briyani / Rice
              </h3>
              <ul className="space-y-2">
                {regularMenu.briyaniRice.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Curry Entrée */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow"
            >
              <h3 className="text-2xl font-bold text-primary mb-4 border-b-2 border-primary pb-2">
                Curry Entrée
              </h3>
              <ul className="space-y-2">
                {regularMenu.curryEntree.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Tandoor */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow"
            >
              <h3 className="text-2xl font-bold text-primary mb-4 border-b-2 border-primary pb-2">
                Tandoor
              </h3>
              <ul className="space-y-2">
                {regularMenu.tandoor.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Breads */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow"
            >
              <h3 className="text-2xl font-bold text-primary mb-4 border-b-2 border-primary pb-2">
                Breads
              </h3>
              <ul className="space-y-2">
                {regularMenu.breads.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Desserts */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow"
            >
              <h3 className="text-2xl font-bold text-primary mb-4 border-b-2 border-primary pb-2">
                Desserts
              </h3>
              <ul className="space-y-2">
                {regularMenu.desserts.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Drinks */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow"
            >
              <h3 className="text-2xl font-bold text-primary mb-4 border-b-2 border-primary pb-2">
                Drinks
              </h3>
              <ul className="space-y-2">
                {regularMenu.drinks.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Condiments */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow"
            >
              <h3 className="text-2xl font-bold text-primary mb-4 border-b-2 border-primary pb-2">
                Condiments
              </h3>
              <ul className="space-y-2">
                {regularMenu.condiments.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Special Menu Section */}
      <section className="py-16 bg-gradient-to-b from-primary/5 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-headingColor mb-4">
              Special Menu - Weekly Specials & Premium Catering
            </h2>
            <p className="text-lg text-textColor max-w-2xl mx-auto">
              Rotating specialties and premium selections for your special occasions
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Special Appetizers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-white to-primary/5 rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow border border-primary/20"
            >
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-primary rounded-full mr-3"></div>
                <h3 className="text-2xl font-bold text-primary">
                  Appetizers
                </h3>
              </div>
              <ul className="space-y-2">
                {specialMenu.appetizers.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">★</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Special Curry */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-white to-primary/5 rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow border border-primary/20"
            >
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-primary rounded-full mr-3"></div>
                <h3 className="text-2xl font-bold text-primary">
                  Curry Specials
                </h3>
              </div>
              <ul className="space-y-2">
                {specialMenu.curry.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">★</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Special Tandoor */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-white to-primary/5 rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow border border-primary/20"
            >
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-primary rounded-full mr-3"></div>
                <h3 className="text-2xl font-bold text-primary">
                  Tandoor
                </h3>
              </div>
              <ul className="space-y-2">
                {specialMenu.tandoor.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">★</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Special Briyani */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-white to-primary/5 rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow border border-primary/20"
            >
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-primary rounded-full mr-3"></div>
                <h3 className="text-2xl font-bold text-primary">
                  Briyani
                </h3>
              </div>
              <ul className="space-y-2">
                {specialMenu.briyani.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">★</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Special Rice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-white to-primary/5 rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow border border-primary/20"
            >
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-primary rounded-full mr-3"></div>
                <h3 className="text-2xl font-bold text-primary">
                  Rice Varieties
                </h3>
              </div>
              <ul className="space-y-2">
                {specialMenu.rice.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">★</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Special Desserts */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-br from-white to-primary/5 rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow border border-primary/20"
            >
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-primary rounded-full mr-3"></div>
                <h3 className="text-2xl font-bold text-primary">
                  Desserts
                </h3>
              </div>
              <ul className="space-y-2">
                {specialMenu.desserts.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">★</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Special Breads */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-white to-primary/5 rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow border border-primary/20"
            >
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-primary rounded-full mr-3"></div>
                <h3 className="text-2xl font-bold text-primary">
                  Breads
                </h3>
              </div>
              <ul className="space-y-2">
                {specialMenu.breads.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">★</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Special Drinks */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-white to-primary/5 rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow border border-primary/20"
            >
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-primary rounded-full mr-3"></div>
                <h3 className="text-2xl font-bold text-primary">
                  Drinks
                </h3>
              </div>
              <ul className="space-y-2">
                {specialMenu.drinks.map((item, i) => (
                  <li key={i} className="flex items-start text-textColor">
                    <span className="text-primary mr-2">★</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Big CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary via-primaryDark to-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              Ready to Make Your Event Unforgettable?
            </h2>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              Let us bring the authentic taste of India to your special occasion
            </p>
            <p className="text-lg mb-12 max-w-2xl mx-auto opacity-80">
              Whether it's a wedding, corporate event, birthday party, or any celebration, 
              our expert team is here to make it memorable with delicious food and exceptional service.
            </p>

            <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-12">
              <motion.a
                href="tel:+18576055777"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group flex items-center justify-center gap-3 bg-white text-primary px-10 py-5 rounded-full text-xl font-bold shadow-2xl hover:shadow-3xl transition-all w-full md:w-auto"
              >
                <IoCall className="text-3xl group-hover:rotate-12 transition-transform" />
                <span>Call Now: (857) 605-5777</span>
              </motion.a>

              <motion.a
                href="mailto:info@royalcurryfish.com"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm text-white border-2 border-white px-10 py-5 rounded-full text-xl font-bold hover:bg-white hover:text-primary transition-all w-full md:w-auto"
              >
                <IoMail className="text-3xl group-hover:scale-110 transition-transform" />
                <span>Email Us</span>
              </motion.a>
            </div>

            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <div className="text-4xl font-bold mb-2">500+</div>
                <div className="text-lg opacity-90">Events Catered</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <div className="text-4xl font-bold mb-2">50+</div>
                <div className="text-lg opacity-90">Menu Items</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <div className="text-4xl font-bold mb-2">100%</div>
                <div className="text-lg opacity-90">Satisfaction</div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-white/20">
              <p className="text-lg mb-4">
                <strong>Open Daily:</strong> 11:30 AM - 9:00 PM
              </p>
              <p className="text-base opacity-80">
                Customizable packages available • Flexible catering options • Professional staff • Fresh ingredients
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <DecorativeElement type="divider" className="w-full max-w-7xl mx-auto" />
    </div>
  );
};

export default CateringPage;
